import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { localeUI } from '../lib/format';
import { Clock4, Coffee, MapPin, Play, Square, Undo2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * El reloj de fichaje en la barra de arriba.
 *
 * Se muestra SOLO si el reloj está habilitado para este usuario (`enabled`): a quien no ficha no
 * se le pone un botón que no puede usar. Si está habilitado pero la puerta de red lo bloquea, el
 * botón igual aparece —en gris y con el motivo— porque "no me sale el botón" y "no me deja fichar
 * desde casa" son problemas distintos y el empleado tiene que poder distinguirlos.
 */

type PunchKind = 'in' | 'break_start' | 'break_end' | 'out';
type Shift = {
  id: string; status: 'open' | 'on_break' | 'closed' | 'missing_out' | 'pending_review' | 'void';
  started_at: string; started_local: string; ended_at: string | null;
  worked_minutes: number | null; break_minutes: number; paid_minutes: number | null;
  late_minutes: number; needs_review: boolean;
  punches: Array<{ id: string; kind: PunchKind; at: string; local_at: string }>;
};
type State = {
  enabled: boolean; blocked_reason: string | null;
  location: { id: string; name: string; tz: string } | null;
  today: string | null; shift: Shift | null; allowed: PunchKind[]; closed_minutes_today: number;
};

/** hh:mm a partir de minutos. Se usa para totales, no para relojes en vivo. */
function hhmm(min: number): string {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

export function ClockButton() {
  const { t } = useI18n();
  const [st, setSt] = useState<State | null>(null);
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const box = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try { setSt(await api.get<State>('/api/timeclock/state')); } catch { /* silencioso: no rompe la barra */ }
  }, []);

  useEffect(() => { void load(); }, [load]);
  // Refresco periódico: si fichaste desde el teléfono, la pestaña abierta se entera sola.
  useEffect(() => {
    const h = setInterval(() => { if (!document.hidden) void load(); }, 60_000);
    const alVolver = () => { if (!document.hidden) void load(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { clearInterval(h); document.removeEventListener('visibilitychange', alVolver); };
  }, [load]);
  // El contador de "llevás X trabajando" avanza solo mientras el turno esté vivo.
  useEffect(() => {
    if (st?.shift?.status !== 'open' && st?.shift?.status !== 'on_break') return;
    const h = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(h);
  }, [st?.shift?.status]);
  /* CIERRE DE SESIÓN tras el clock out. El aviso es visible y cancelable: nadie tiene que
     descubrir que la app lo echó. */
  const [salidaEn, setSalidaEn] = useState<number | null>(null);
  const [faltan, setFaltan] = useState(0);
  useEffect(() => {
    if (salidaEn == null) { setFaltan(0); return undefined; }
    const tick = () => {
      const restan = Math.max(0, Math.ceil((salidaEn - Date.now()) / 1000));
      setFaltan(restan);
      if (restan === 0) { setSalidaEn(null); void logout(); }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [salidaEn, logout]);

  // Cerrar al hacer click afuera.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  /**
   * MINUTOS DE GRACIA entre el clock out y el cierre de sesión.
   *
   * El que fichó la salida ya no está trabajando, y dejar la sesión abierta en una máquina
   * compartida es el problema real. Pero cerrar en el acto es hostil: casi siempre queda algo
   * abierto sin guardar, y a veces se ficha por error y se vuelve a entrar en el momento.
   * Diez minutos alcanzan para las dos cosas — y si vuelve a fichar, se cancela.
   */
  const MIN_GRACIA = 10;

  async function doPunch(kind: PunchKind) {
    setBusy(true); setErr(null);
    try {
      await api.post('/api/timeclock/punch', { kind });
      if (kind === 'out') setSalidaEn(Date.now() + MIN_GRACIA * 60_000);
      else setSalidaEn(null); // volvió a fichar: se cancela la cuenta regresiva
      await load();
    } catch (e) {
      const raw = e instanceof ApiError ? e.message : 'Error';
      // El backend manda motivos con nombre; se traducen y si aparece uno nuevo se muestra crudo
      // en vez de tragárselo (un fichaje que "no hace nada" es peor que un mensaje feo).
      setErr(t(`clock.err.${raw}`) === `clock.err.${raw}` ? raw : t(`clock.err.${raw}`));
    } finally { setBusy(false); }
  }

  if (!st?.enabled) return null;

  const sh = st.shift;
  const vivo = sh?.status === 'open' || sh?.status === 'on_break';
  const enBreak = sh?.status === 'on_break';
  const bloqueado = Boolean(st.blocked_reason);

  // Minutos corridos del turno vivo: desde la entrada hasta ahora, menos los breaks ya cerrados.
  // `tick` está en las dependencias para que esto se recalcule con el intervalo.
  void tick;
  const corridos = vivo && sh
    ? Math.max(0, Math.round((Date.now() - new Date(sh.started_at).getTime()) / 60000) - (sh.break_minutes ?? 0))
    : 0;
  const totalHoy = st.closed_minutes_today + corridos;

  const color = bloqueado ? 'var(--muted)' : enBreak ? 'var(--orange)' : vivo ? 'var(--green)' : 'var(--muted)';
  // Solo el reloj + punto de estado (pedido 2026-08-10: "Clock out" se envolvía en dos líneas y
  // se veía mal). El contador hh:mm sí se muestra con el turno vivo — es corto y es el dato útil;
  // la palabra del estado vive en el tooltip.
  const etiqueta = vivo && !enBreak ? hhmm(corridos) : null;
  const estadoTxt = bloqueado ? t('clock.blocked') : enBreak ? t('clock.onBreak') : vivo ? t('clock.in') : t('clock.out');

  const Boton = ({ kind, label, primary }: { kind: PunchKind; label: ReactNode; primary?: boolean }) => (
    <button
      className={`btn${primary ? '' : ' ghost'} sm`}
      disabled={busy || !st.allowed.includes(kind)}
      onClick={() => void doPunch(kind)}
      style={primary ? { background: kind === 'out' ? 'var(--red)' : 'var(--green)', color: '#fff', borderColor: 'transparent' } : {}}
    >{label}</button>
  );

  return (
    <div ref={box} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn ghost sm"
        onClick={() => setOpen((v) => !v)}
        title={`${t('clock.title')} · ${estadoTxt}${st.location ? ` · ${st.location.name}` : ''}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        <span style={{ width: 7, height: 7, borderRadius: 999, background: color, display: 'inline-block' }} />
        {enBreak ? <Coffee size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> : <Clock4 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />}
        {etiqueta}
      </button>

      {open && (
        <div
          className="card"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60, width: 260,
            display: 'grid', gap: 10, padding: 12, boxShadow: 'var(--shadow-lg, 0 8px 28px rgba(0,0,0,.18))',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: 'var(--fs-sm)' }}>{t('clock.title')}</strong>
            {st.location && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', marginLeft: 'auto' }}><MapPin size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {st.location.name}</span>}
          </div>

          {bloqueado && (
            <div style={{ fontSize: 'var(--fs-2xs)', padding: '6px 8px', borderRadius: 8, background: 'rgba(185,28,28,.10)', color: 'var(--red)' }}>
              {t(`clock.blk.${st.blocked_reason}`) === `clock.blk.${st.blocked_reason}` ? st.blocked_reason : t(`clock.blk.${st.blocked_reason}`)}
            </div>
          )}

          <div style={{ display: 'grid', gap: 3, fontSize: 'var(--fs-xs)' }}>
            <div style={{ display: 'flex' }}>
              <span className="muted">{t('clock.todayTotal')}</span>
              <strong style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{hhmm(totalHoy)}</strong>
            </div>
            {vivo && sh && (
              <>
                <div style={{ display: 'flex' }}>
                  <span className="muted">{t('clock.since')}</span>
                  <span style={{ marginLeft: 'auto' }}>{new Date(sh.started_at).toLocaleTimeString(localeUI(), { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {sh.break_minutes > 0 && (
                  <div style={{ display: 'flex' }}>
                    <span className="muted">{t('clock.breaks')}</span>
                    <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{hhmm(sh.break_minutes)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {err && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)' }}>{err}</div>}

          {/* La sesión se va a cerrar sola. Se avisa y se puede cancelar: que la app te eche sin
              decirte nada es lo que hace que la gente desconfíe del reloj. */}
          {faltan > 0 && (
            <div style={{ fontSize: 'var(--fs-2xs)', background: 'var(--orange-soft)', color: 'var(--orange)', borderRadius: 8, padding: '7px 9px', display: 'grid', gap: 5 }}>
              <span>{t('clock.logoutIn').replace('{m}', `${Math.floor(faltan / 60)}:${String(faltan % 60).padStart(2, '0')}`)}</span>
              <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setSalidaEn(null)}>{t('clock.stayIn')}</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!vivo && <Boton kind="in" label={<><Play size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('clock.in')}</>} primary />}
            {vivo && !enBreak && <Boton kind="break_start" label={<><Coffee size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('clock.breakStart')}</>} />}
            {enBreak && <Boton kind="break_end" label={<><Undo2 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('clock.breakEnd')}</>} />}
            {vivo && <Boton kind="out" label={<><Square size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('clock.out')}</>} primary />}
          </div>

          <a href="/my-hours" className="btn ghost sm" style={{ textAlign: 'center', fontSize: 'var(--fs-2xs)' }}>{t('clock.myHours')} →</a>
        </div>
      )}
    </div>
  );
}
